import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Stack,
  Tooltip
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Email,
  Phone,
  Verified,
  Pending
} from '@mui/icons-material';

interface Contact {
  id: string;
  contactType: 'email' | 'phone';
  contactValue: string;
  isPrimary: boolean;
  isVerified: boolean;
  isActive: boolean;
  verificationToken?: string;
  verificationExpiresAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface ContactManagerProps {
  userId: string;
  onContactUpdate?: () => void;
}

const ContactManager: React.FC<ContactManagerProps> = ({ userId, onContactUpdate }) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });

  const [newContact, setNewContact] = useState({
    contactType: 'email' as 'email' | 'phone',
    contactValue: '',
    isPrimary: false
  });

  useEffect(() => {
    loadContacts();
  }, [userId]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/contacts', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to load contacts');
      }
      
      const data = await response.json();
      setContacts(data.data || []);
    } catch (err) {
      setError('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleAddContact = async () => {
    try {
      const response = await fetch('/api/v1/contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newContact)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add contact');
      }
      
      setSnackbar({
        open: true,
        message: 'Contact added successfully',
        severity: 'success'
      });
      
      setOpenAddDialog(false);
      setNewContact({ contactType: 'email', contactValue: '', isPrimary: false });
      loadContacts();
      onContactUpdate?.();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to add contact',
        severity: 'error'
      });
    }
  };

  const handleEditContact = async () => {
    if (!editingContact) return;

    try {
      const response = await fetch(`/api/v1/contacts/${editingContact.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          contactValue: editingContact.contactValue,
          isPrimary: editingContact.isPrimary
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update contact');
      }
      
      setSnackbar({
        open: true,
        message: 'Contact updated successfully',
        severity: 'success'
      });
      
      setOpenEditDialog(false);
      setEditingContact(null);
      loadContacts();
      onContactUpdate?.();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to update contact',
        severity: 'error'
      });
    }
  };

  const handleDeleteContact = async (contactId: string) => {
    try {
      const response = await fetch(`/api/v1/contacts/${contactId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete contact');
      }
      
      setSnackbar({
        open: true,
        message: 'Contact deleted successfully',
        severity: 'success'
      });
      
      loadContacts();
      onContactUpdate?.();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to delete contact',
        severity: 'error'
      });
    }
  };

  const handleSetPrimary = async (contactId: string) => {
    try {
      const response = await fetch(`/api/v1/contacts/${contactId}/primary`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update primary contact');
      }
      
      setSnackbar({
        open: true,
        message: 'Primary contact updated',
        severity: 'success'
      });
      
      loadContacts();
      onContactUpdate?.();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to update primary contact',
        severity: 'error'
      });
    }
  };

  const handleResendVerification = async (contactId: string) => {
    try {
      const response = await fetch(`/api/v1/contacts/${contactId}/resend-verification`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send verification');
      }
      
      setSnackbar({
        open: true,
        message: 'Verification email sent',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to send verification',
        severity: 'error'
      });
    }
  };

  const getContactIcon = (type: string) => {
    switch (type) {
      case 'email': return <Email />;
      case 'phone': return <Phone />;
      default: return <Email />;
    }
  };

  const getContactTypeLabel = (type: string) => {
    switch (type) {
      case 'email': return 'Email';
      case 'phone': return 'Phone';
      default: return type;
    }
  };

  if (loading) {
    return <Typography>Loading contacts...</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Contact Information</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setOpenAddDialog(true)}
        >
          Add Contact Information
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={2}>
        {contacts.map((contact) => (
          <Card key={contact.id} variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {getContactIcon(contact.contactType)}
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      {contact.contactValue}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getContactTypeLabel(contact.contactType)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {contact.isPrimary && (
                    <Chip label="Primary" color="primary" size="small" />
                  )}
                  
                  {contact.isVerified ? (
                    <Tooltip title="Verified">
                      <Verified color="success" />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Not verified">
                      <Pending color="warning" />
                    </Tooltip>
                  )}

                  {!contact.isVerified && contact.contactType === 'email' && (
                    <Button
                      size="small"
                      onClick={() => handleResendVerification(contact.id)}
                    >
                      Resend Verification
                    </Button>
                  )}

                  {!contact.isPrimary && (
                    <Button
                      size="small"
                      onClick={() => handleSetPrimary(contact.id)}
                    >
                      Set Primary
                    </Button>
                  )}

                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditingContact(contact);
                      setOpenEditDialog(true);
                    }}
                  >
                    <Edit />
                  </IconButton>

                  {!contact.isPrimary && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteContact(contact.id)}
                    >
                      <Delete />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}

        {contacts.length === 0 && (
          <Alert severity="info">
            No contacts found. Add your first contact method.
          </Alert>
        )}
      </Stack>

      {/* Add Contact Dialog */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Contact</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Contact Type</InputLabel>
              <Select
                value={newContact.contactType}
                onChange={(e) => setNewContact(prev => ({ ...prev, contactType: e.target.value as any }))}
                label="Contact Type"
              >
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="phone">Phone</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label={getContactTypeLabel(newContact.contactType)}
              value={newContact.contactValue}
              onChange={(e) => setNewContact(prev => ({ ...prev, contactValue: e.target.value }))}
              placeholder={
                newContact.contactType === 'email' 
                  ? 'user@example.com' 
                  : '+1 (555) 123-4567'
              }
            />

            <FormControlLabel
              control={
                <input
                  type="checkbox"
                  checked={newContact.isPrimary}
                  onChange={(e) => setNewContact(prev => ({ ...prev, isPrimary: e.target.checked }))}
                />
              }
              label="Set as primary contact"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAddContact} 
            variant="contained"
            disabled={!newContact.contactValue}
          >
            Add Contact Information
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Contact Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Contact</DialogTitle>
        <DialogContent>
          {editingContact && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                fullWidth
                label={getContactTypeLabel(editingContact.contactType)}
                value={editingContact.contactValue}
                onChange={(e) => setEditingContact(prev => prev ? { ...prev, contactValue: e.target.value } : null)}
              />

              <FormControlLabel
                control={
                  <input
                    type="checkbox"
                    checked={editingContact.isPrimary}
                    onChange={(e) => setEditingContact(prev => prev ? { ...prev, isPrimary: e.target.checked } : null)}
                  />
                }
                label="Set as primary contact"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleEditContact} 
            variant="contained"
            disabled={!editingContact?.contactValue}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactManager;
